/**
 * Hook wrapper around gridRefreshRef.js's capture/trigger mechanics, so a
 * caller can refetch an EntityGrid after a mutation without storing the
 * grid's refresh function in component state.
 *
 * Contains:
 * - useGridRefreshRef — returns the onGridReady/refresh pair to wire into EntityGrid
 */

import { useCallback, useMemo, useRef } from 'react';

import { captureGridRefresh, triggerGridRefresh } from '@sophocles/data-grids/datasource/gridRefreshRef';

/**
 * Captures an EntityGrid's page-0 refresh trigger from its `onGridReady`
 * callback into a stable ref, so a caller can refetch after a mutation
 * without storing the grid's refresh function in state.
 *
 * @returns {{ onGridReady: (api: { refresh: Function }) => void, refresh: () => void }}
 *   onGridReady — pass to `EntityGrid`'s `options.onGridReady` (or wrap it
 *     alongside other onGridReady work a caller needs).
 *   refresh — call after a mutation to refetch page 0; a no-op before the
 *     grid has mounted.
 */
export default function useGridRefreshRef() {
  const refreshFnRef = useRef(null);

  const onGridReady = useCallback(api => captureGridRefresh(refreshFnRef, api), []);
  const refresh = useCallback(() => triggerGridRefresh(refreshFnRef), []);

  return useMemo(() => ({ onGridReady, refresh }), [onGridReady, refresh]);
}
